/* Pulse card gallery — pairs each `.pulse-card-gallery__track` with its
   pagination so the active line follows the visible slide.
   Idempotent: safe to call repeatedly (skips already-bound elements).
   Re-runs on shopify:section:load so editor re-renders work. */
(function () {
  /* RTL helpers. In RTL pages browsers report scrollLeft as NEGATIVE
     when the track is scrolled toward the inline-end. To compute a
     slide index we take the absolute value; to scroll TO a slide we
     negate the target so the browser interprets it correctly. */
  function isRTL(el) {
    return getComputedStyle(el).direction === 'rtl';
  }
  function trackIdx(track, slideWidth) {
    return Math.round(Math.abs(track.scrollLeft) / slideWidth);
  }
  function trackScrollToIdx(track, idx, slideWidth) {
    var target = idx * slideWidth;
    if (isRTL(track)) target = -target;
    track.scrollTo({ left: target, behavior: 'smooth' });
  }

  function bindGallery(gallery) {
    if (!gallery || gallery.dataset.bound === 'true') return;
    var track = gallery.querySelector('[data-pulse-card-track]');
    var pagination = gallery.querySelector('[data-pulse-card-pagination]');
    if (!track) return;
    gallery.dataset.bound = 'true';

    var lines = pagination
      ? Array.prototype.slice.call(pagination.querySelectorAll('.pulse-card-gallery__line'))
      : [];

    var ticking = false;
    function update() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        ticking = false;
        if (!track.offsetWidth) return;
        var idx = trackIdx(track, track.offsetWidth);
        if (idx < 0) idx = 0;
        if (idx >= lines.length) idx = lines.length - 1;
        for (var i = 0; i < lines.length; i++) {
          lines[i].classList.toggle('is-active', i === idx);
        }
      });
    }

    /* Returns true when the gallery is currently locked off by an outer
       slider (slider-mode CSS sets `pointer-events: none`). Any handler
       that fires despite the lock should bail via this — defends against
       Safari quirks where touch events occasionally bubble up from
       elements with inherited pointer-events: none. */
    function isLocked() {
      return window.getComputedStyle &&
        window.getComputedStyle(gallery).pointerEvents === 'none';
    }

    track.addEventListener('scroll', update, { passive: true });

    /* ── Touch tracking (mobile swipe support) ──
       Mouse drag-to-scroll has been removed by request — desktop users
       navigate via the chevron buttons or horizontal wheel/trackpad pan.
       Touch is still tracked here so a swipe that crosses into a real
       click (small movement on touchend) can be classified and used to
       suppress the parent <a>'s navigation. */
    var startX = 0;
    var startY = 0;
    var moved = false;

    function onTouchStart(e) {
      if (isLocked()) return;
      var pt = e.touches[0];
      startX = pt.clientX;
      startY = pt.clientY;
      moved = false;
    }
    function onTouchMove(e) {
      if (isLocked()) return;
      var pt = e.touches[0];
      if (Math.abs(pt.clientX - startX) > 10 || Math.abs(pt.clientY - startY) > 10) {
        moved = true;
      }
    }
    function onClick(e) {
      // If the touch moved meaningfully before release, this was a swipe
      // not a tap — kill the parent <a>'s navigation.
      if (moved) {
        e.preventDefault();
        e.stopPropagation();
        moved = false;
      }
    }
    track.addEventListener('touchstart', onTouchStart, { passive: true });
    track.addEventListener('touchmove', onTouchMove, { passive: true });
    track.addEventListener('click', onClick, true);

    /* Per-element chevron clicks are handled at the document level (see
       below) — that path uses `elementsFromPoint` and runs in capture
       phase, which bypasses the stacking-context / pointer-events trap
       caused by Dawn's full-card link `::after` overlay (collection
       page, related-products) and the slider-mode `pointer-events:
       none` lockouts. Binding here as a fallback would race with the
       document handler and double-step. */

    /* Horizontal trackpad / shift-scroll wheel handling.
       Capture-phase listener (third arg `true`) means we run BEFORE any
       ancestor's bubble-phase listener, so even if a parent grid would
       otherwise eat the gesture (collection page!), the gallery wins.
       Vertical wheel passes through normally to scroll the page. */
    function onWheel(e) {
      if (isLocked()) return;
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return; // vertical intent — let it bubble
      var maxLeft = track.scrollWidth - track.offsetWidth;
      if (maxLeft <= 0) return; // nothing to scroll
      track.scrollLeft += e.deltaX;
      e.preventDefault();
      e.stopPropagation();
    }
    track.addEventListener('wheel', onWheel, { passive: false, capture: true });
    /* Also bind to the gallery wrapper as a safety net — some hover targets
       (like the image itself) may have wheel events that don't bubble cleanly
       through to `track`. Listening on the gallery (capture phase) catches
       any wheel event over its area. */
    gallery.addEventListener('wheel', onWheel, { passive: false, capture: true });
    // Block native image dragging which would otherwise hijack the gesture
    track.querySelectorAll('img').forEach(function (img) {
      img.addEventListener('dragstart', function (e) { e.preventDefault(); });
    });
  }

  function init(root) {
    var galleries = (root || document).querySelectorAll('[data-pulse-card-gallery]');
    for (var i = 0; i < galleries.length; i++) bindGallery(galleries[i]);
  }

  /* ── Swipe hint (collection page only) ──
     One-shot affordance: on entering a collection page, the FIRST card
     gallery with multiple images briefly nudges its track left, then
     glides back. Tells visitors "yes, you can swipe through these."
     Skipped if `prefers-reduced-motion` is set or the merchant turned
     the hint off. Waits for the preloader to fade out first so the
     animation isn't hidden behind it. */
  var swipeHintFired = false;
  function actuallyPlayHint() {
    if (swipeHintFired) return;
    var galleries = document.querySelectorAll('[data-pulse-card-gallery]');
    for (var i = 0; i < galleries.length; i++) {
      var track = galleries[i].querySelector('[data-pulse-card-track]');
      if (!track || !track.offsetWidth) continue;
      var maxLeft = track.scrollWidth - track.offsetWidth;
      if (maxLeft <= 0) continue;
      swipeHintFired = true;
      var nudge = Math.min(maxLeft, Math.round(track.offsetWidth * 0.25));
      (function (t, n) {
        setTimeout(function () {
          // Disable scroll-snap during the hint. With `scroll-snap-type:
          // x mandatory`, scrolling to 25% would immediately snap back to 0
          // (closest snap point), making the animation invisible. We reuse
          // the `is-dragging` class because its CSS already turns snap off.
          t.classList.add('is-dragging');
          // Negate target in RTL so the hint nudges toward inline-end
          // (the direction "next slide" lives in) regardless of language.
          var hintTarget = isRTL(t) ? -n : n;
          t.scrollTo({ left: hintTarget, behavior: 'smooth' });
          setTimeout(function () {
            t.scrollTo({ left: 0, behavior: 'smooth' });
            // Re-enable snap after the return scroll has settled. Done
            // here (not on `scrollend`) because `scrollend` lacks Safari
            // support — the timeout is a portable equivalent.
            setTimeout(function () {
              t.classList.remove('is-dragging');
            }, 600);
          }, 700);
        }, 250);
      })(track, nudge);
      return;
    }
  }
  function playSwipeHint() {
    if (!window.PulseSwipeHintEnabled) return;
    if (!document.body || !document.body.classList.contains('template-collection')) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    // If a preloader is on the page, wait until it has fully removed
    // itself (body gets `pulse-preloader-done`). Without this, the hint
    // animation plays behind the preloader overlay and the user never
    // sees it.
    var hasPreloader = !!document.querySelector('.pulse-preloader');
    if (!hasPreloader || document.body.classList.contains('pulse-preloader-done')) {
      actuallyPlayHint();
      return;
    }
    var observer = new MutationObserver(function () {
      if (document.body.classList.contains('pulse-preloader-done')) {
        observer.disconnect();
        actuallyPlayHint();
      }
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    // Hard fallback: play after 5s no matter what.
    setTimeout(function () {
      observer.disconnect();
      actuallyPlayHint();
    }, 5000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); playSwipeHint(); });
  } else {
    init();
    playSwipeHint();
  }
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });

  /* ── Document-level TOUCH-step catcher (mobile) ──
     Same problem as the wheel handler: the card's `<a>::after` pseudo is
     the touch target, not the gallery underneath, so native horizontal
     scroll on the track never engages. We resolve via `elementsFromPoint`,
     decide whether the gesture is horizontal early (~8px), and step one
     slide per swipe. preventDefault on horizontal intent stops the page
     from scrolling sideways with the gesture; vertical intent passes
     through so page-scroll works normally. */
  var touchState = null;
  document.addEventListener('touchstart', function (e) {
    if (e.touches.length !== 1) { touchState = null; return; }
    var touch = e.touches[0];
    var stack = (typeof document.elementsFromPoint === 'function')
      ? document.elementsFromPoint(touch.clientX, touch.clientY)
      : [e.target];

    // If the topmost element under the touch is inside another
    // horizontally-scrolling control (the open quick-buy size-pill row,
    // a swatch row, etc.), let THAT element handle the swipe. Without
    // this guard, the gallery sitting underneath the open panel is
    // still found in the elementsFromPoint stack and our touchmove
    // preventDefault would block the panel's native horizontal scroll.
    var topTarget = stack[0];
    if (topTarget && topTarget.closest &&
        topTarget.closest('.card-grid-options-size, .card-grid-centered, .card_colors')) {
      touchState = null;
      return;
    }

    var gallery = null;
    for (var i = 0; i < stack.length; i++) {
      var el = stack[i];
      if (!el || !el.closest) continue;
      var match = el.closest('.pulse-card-gallery');
      if (match) { gallery = match; break; }
    }
    if (!gallery) { touchState = null; return; }

    // Bail out if this gallery is currently "locked" — i.e., the outer
    // section has put it in slider mode, where the gallery has
    // `pointer-events: none` and the inner track is `overflow-x: hidden`
    // with extra slides `display: none`. Without these guards, our
    // preventDefault in touchmove would also block the outer slider's
    // swipe gesture.
    var galleryPE = window.getComputedStyle ? window.getComputedStyle(gallery).pointerEvents : '';
    if (galleryPE === 'none') { touchState = null; return; }
    var track = gallery.querySelector('[data-pulse-card-track]');
    if (!track) { touchState = null; return; }
    if (track.scrollWidth <= track.offsetWidth + 1) { touchState = null; return; }

    touchState = {
      gallery: gallery,
      startX: touch.clientX,
      startY: touch.clientY,
      intent: null
    };
  }, { passive: true, capture: true });

  document.addEventListener('touchmove', function (e) {
    if (!touchState || e.touches.length !== 1) return;
    var touch = e.touches[0];
    var dx = touch.clientX - touchState.startX;
    var dy = touch.clientY - touchState.startY;
    if (touchState.intent === null) {
      // Decide intent as soon as movement exceeds 8px in either axis.
      if (Math.abs(dx) > 8 || Math.abs(dy) > 8) {
        touchState.intent = Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical';
      }
    }
    if (touchState.intent === 'horizontal') {
      // Stop the page from scrolling horizontally with the gesture.
      e.preventDefault();
    }
  }, { passive: false, capture: true });

  document.addEventListener('touchend', function (e) {
    if (!touchState) return;
    var state = touchState;
    touchState = null;
    if (state.intent !== 'horizontal') return;
    var touch = (e.changedTouches && e.changedTouches[0]);
    if (!touch) return;
    var dx = touch.clientX - state.startX;
    if (Math.abs(dx) < 30) return; // not a confident swipe
    var track = state.gallery.querySelector('[data-pulse-card-track]');
    if (!track || !track.offsetWidth) return;
    var maxLeft = track.scrollWidth - track.offsetWidth;
    if (maxLeft <= 0) return;
    var slideWidth = track.offsetWidth;
    var currentIdx = trackIdx(track, slideWidth);
    // Swipe right→left (dx < 0) = next slide in BOTH LTR and RTL — the
    // gesture's relationship to "next" is the same (toward the inline-end
    // edge of the carousel). Only the scrollLeft sign differs, which
    // trackScrollToIdx handles internally.
    var direction = dx < 0 ? 1 : -1;
    var slides = track.querySelectorAll('.pulse-card-gallery__slide');
    var nextIdx = Math.max(0, Math.min(currentIdx + direction, slides.length - 1));
    if (nextIdx === currentIdx) return;
    trackScrollToIdx(track, nextIdx, slideWidth);
  }, { passive: true, capture: true });

  /* ── Document-level wheel-step catcher ──
     Why this is here:
       1. Per-element wheel listeners can be bypassed by an ancestor scroll
          container catching the gesture first (collection page grid).
       2. Collection-page cards have a `<a>::after` pseudo absolutely
          covering the card for clickability; `event.target` is the LINK,
          so `closest('.pulse-card-gallery')` returns null.
       3. `scroll-snap-type: x mandatory` was fighting small wheel deltas:
          a tiny scroll would happen, then snap back to the same slide,
          making swipes feel like they "didn't work."

     Solution: find the gallery via `elementsFromPoint` (sees through the
     link overlay), then SLIDE-STEP — each horizontal wheel "burst" moves
     exactly one slide, throttled so a continuous trackpad swipe doesn't
     skip past everything. Behaves like a mobile carousel. */
  var wheelLockUntil = 0;
  document.addEventListener('wheel', function (e) {
    // Only intercept clearly-horizontal intent. Small noise on the perpendicular
    // axis is tolerated via the 1.2x multiplier.
    if (Math.abs(e.deltaX) < Math.abs(e.deltaY) * 1.2) return;
    if (Math.abs(e.deltaX) < 4) return; // ignore micro-noise

    var gallery = null;
    var stack = (typeof document.elementsFromPoint === 'function')
      ? document.elementsFromPoint(e.clientX, e.clientY)
      : [e.target];
    for (var i = 0; i < stack.length; i++) {
      var el = stack[i];
      if (!el || !el.closest) continue;
      var match = el.closest('.pulse-card-gallery');
      if (match) { gallery = match; break; }
    }
    if (!gallery) return;

    // Bail out when the gallery is locked by an outer slider (slider mode
    // sets `pointer-events: none` on the gallery). Without this, our wheel
    // handler would still consume horizontal wheel deltas over the card and
    // block the outer slider from receiving them.
    var galleryPE = window.getComputedStyle ? window.getComputedStyle(gallery).pointerEvents : '';
    if (galleryPE === 'none') return;

    var track = gallery.querySelector('[data-pulse-card-track]');
    if (!track || !track.offsetWidth) return;
    var maxLeft = track.scrollWidth - track.offsetWidth;
    if (maxLeft <= 0) return;

    // Always consume the gesture so the page never back-navigates from this card.
    e.preventDefault();
    e.stopPropagation();

    // Throttle: ignore wheel events for ~360ms after a step (one slide per swipe).
    var now = Date.now();
    if (now < wheelLockUntil) return;

    var slideWidth = track.offsetWidth;
    var currentIdx = trackIdx(track, slideWidth);
    // Trackpad/mouse-wheel deltaX is physical (positive = scroll right).
    // In LTR, scroll-right = next. In RTL, scroll-right = previous (next
    // is on the left). Flip direction in RTL so wheel intent matches
    // visual flow.
    var direction = e.deltaX > 0 ? 1 : -1;
    if (isRTL(track)) direction = -direction;
    var slides = track.querySelectorAll('.pulse-card-gallery__slide');
    var maxIdx = slides.length - 1;
    var nextIdx = Math.max(0, Math.min(currentIdx + direction, maxIdx));
    if (nextIdx === currentIdx) return; // already at edge

    wheelLockUntil = now + 360;
    trackScrollToIdx(track, nextIdx, slideWidth);
  }, { passive: false, capture: true });

  /* ── Document-level CHEVRON click catcher ──
     Why this is here:
       1. Dawn's `.card__heading a::after` overlay (z-index: 1) covers
          the entire card on the collection page and related-products
          section, swallowing every click that lands on a chevron.
       2. Slider-mode lockouts (Featured Collection / Related Products)
          set `pointer-events: none` on the gallery and all descendants,
          so per-element click listeners never fire there either.

     Both problems disappear if we listen on `document` in capture phase
     and use `elementsFromPoint` to find a chevron under the click —
     `elementsFromPoint` sees through pointer-events and stacking. We
     handle the step ourselves and `preventDefault` so the link doesn't
     navigate. */
  document.addEventListener('click', function (e) {
    if (typeof document.elementsFromPoint !== 'function') return;
    var stack = document.elementsFromPoint(e.clientX, e.clientY);
    var chevron = null;
    for (var i = 0; i < stack.length; i++) {
      var el = stack[i];
      if (!el || !el.classList) continue;
      if (el.classList.contains('pulse-card-gallery__chevron')) {
        chevron = el;
        break;
      }
    }
    if (!chevron) return;

    e.preventDefault();
    e.stopPropagation();

    var gallery = chevron.closest('.pulse-card-gallery');
    if (!gallery) return;
    var track = gallery.querySelector('[data-pulse-card-track]');
    if (!track || !track.offsetWidth) return;
    var slides = track.querySelectorAll('.pulse-card-gallery__slide');
    if (!slides.length) return;

    // Chevron semantics are direction-relative: "prev" always means
    // step toward the start of the gallery, "next" toward the end.
    // No RTL flip needed here — trackScrollToIdx handles the sign.
    var direction = chevron.classList.contains('pulse-card-gallery__chevron--prev') ? -1 : 1;
    var slideWidth = track.offsetWidth;
    var currentIdx = trackIdx(track, slideWidth);
    var nextIdx = currentIdx + direction;
    nextIdx = Math.max(0, Math.min(nextIdx, slides.length - 1));
    trackScrollToIdx(track, nextIdx, slideWidth);
  }, { capture: true });
})();
